import type { ReactElement } from 'react';
import { Button } from 'react-aria-components';
import { setupPage, useSetupPageStale } from '../setup/page';
import { Icon } from '../ui/Icon';
import s from './SetupSandboxBanner.module.css';

/**
 * "Setup sandbox · a practice installation" — on every page of the setup sandbox (APP-096), signed
 * in or not, so nobody mistakes it for the working environment. When the server has refused a
 * change because the sandbox was reset after this page opened, it says so and offers a reload.
 * Nothing at all anywhere else.
 */
export function SetupSandboxBanner(): ReactElement | null {
  const page = setupPage();
  const stale = useSetupPageStale();
  if (page === null) return null;
  return (
    <section className={s.banner} aria-label="Setup sandbox">
      <div className={s.inner}>
        <p className={s.text}>
          <Icon name={stale ? 'alert' : 'tuning'} size={20} />
          <strong>Setup sandbox</strong>
          <span role={stale ? 'alert' : undefined}>
            {stale
              ? 'The sandbox was reset after this page opened, so nothing on it was saved. Reload to continue in the new one.'
              : 'A practice installation. Nothing here reaches the working environment.'}
          </span>
        </p>
        <span className={s.actions}>
          {stale ? (
            <Button
              className={s.action}
              onPress={() => {
                window.location.reload();
              }}
            >
              Reload
            </Button>
          ) : (
            <a className={s.action} href={`${page.workingOrigin}/`}>
              Open working environment
            </a>
          )}
        </span>
      </div>
    </section>
  );
}

/**
 * The sign-in splash's version: the splash is full-bleed, so the sandbox names itself next to the
 * sign-in button instead of in a band above it.
 */
export function SetupSandboxNote(): ReactElement | null {
  if (setupPage() === null) return null;
  return (
    <p className={s.note}>
      <Icon name="tuning" size={18} />
      <span>
        <strong>Setup sandbox</strong> · a practice installation, separate from the working
        environment.
      </span>
    </p>
  );
}
